<template>
  <component
    :is="scroll ? 'o-scroll' : 'div'"
    :id="id"
    ref="radio-group"
    class="p-1"
  >
    <div>
      <div
        v-for="(option, index) in normalizedOptions"
        :key="`${ id || name }-${index}`"
      >
        <radio
          :id="`${ id || name || key }-${index}`"
          v-model="currentValue"
          :value="option.value"
          :name="name"
          :required="required"
          :disabled="disabled"
          :status="status"
          :size="size"
          :class="inputClass"
          :base-class="radioBaseClass"
          :option-class="optionClass"
          :option-class-active="optionClassActive"
          :label="option.text || option.label"
          :info="option.info"
          @blur="onBlur"
          @focus="onFocus"
        >
          <slot name="option" :option="option" :selected="currentValue === option.value" />
        </radio>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import get from 'lodash/get'

import Radio from './Radio.vue'

interface Props {
  id?: string | null
  disabled?: boolean
  name?: string | null
  modelValue?: string | number | boolean | Record<string, any> | null
  required?: boolean
  scroll?: boolean
  baseClass?: string | Record<string, any> | string[]
  radioBaseClass?: string | Record<string, any> | string[]
  defaultStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  inputClass?: string | Record<string, any> | string[]
  labelClass?: string | Record<string, any> | string[]
  optionClass?: string | Record<string, any> | string[]
  optionClassActive?: string | Record<string, any> | string[]
  // multi mixin
  valueAttribute?: string
  textAttribute?: string
  options?: any[] | Record<string, any>
  // classes mixin
  size?: string | null
  status?: boolean | string | null
}

const props = withDefaults(defineProps<Props>(), {
  id: null,
  disabled: false,
  name: null,
  modelValue: null,
  required: false,
  scroll: true,
  baseClass: 'flex flex-col',
  radioBaseClass: undefined,
  defaultStatusClass: '',
  errorStatusClass: 'text-red-700',
  successStatusClass: 'text-green-700',
  warningStatusClass: 'text-yellow-700',
  disabledClass: 'cursor-not-allowed opacity-75',
  defaultSizeClass: '',
  largeSizeClass: 'text-lg',
  smallSizeClass: 'text-sm',
  inputClass: '',
  labelClass: 'w-full ml-2 text-gray-800 flex justify-between items-center',
  optionClass: undefined,
  optionClassActive: undefined,
  valueAttribute: undefined,
  textAttribute: undefined,
  options: () => [],
  size: null,
  status: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
}>()

const key = ref(`${Date.now()}-${Math.random()}`)
const currentValue = ref<any>(props.modelValue)

// -- multi mixin --
function guessOptionValue (option: any) {
  if (props.valueAttribute) return get(option, props.valueAttribute)
  return get(option, 'value', get(option, 'id', get(option, 'text')))
}

function guessOptionText (option: any) {
  if (props.textAttribute) return get(option, props.textAttribute)
  return get(option, 'text', get(option, 'label'))
}

function guessInfoText (option: any) {
  return get(option, 'info', get(option, 'description'))
}

function guessColour (option: any) {
  const colour = get(option, 'colour', get(option, 'background'))
  return colour ? (colour.charAt(0) === '#' ? colour : '#' + colour) : null
}

function normalizeOption (option: any): any {
  if (['string', 'number', 'boolean'].includes(typeof option)) {
    return { value: option, text: option }
  }
  return {
    value: guessOptionValue(option),
    text: guessOptionText(option),
    info: guessInfoText(option),
    colour: guessColour(option),
    control: option.control,
    disabled: option.disabled,
    tooltip: option.tooltip,
    class: option.class,
  }
}

const normalizedOptions = computed(() => {
  if (props.options) {
    if (Array.isArray(props.options)) {
      return props.options.map((option: any) => normalizeOption(option))
    } else {
      return Object.keys(props.options).map(key => ({
        value: key,
        text: (props.options as Record<string, any>)[key],
      }))
    }
  }
  return []
})

// -- classes mixin --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

watch(() => props.modelValue, (value) => {
  currentValue.value = value
})

watch(currentValue, (val) => {
  emit('update:modelValue', val)
  emit('change', val)
})

function onBlur (e: Event) {
  emit('blur', e)
}

function onFocus (e: Event) {
  emit('focus', e)
}
</script>
